
import Vue from 'vue';

import SiteLuminaireList from '@/components/configuration/SiteLuminaireList.vue';
import SiteSensorList from '@/components/configuration/SiteSensorList.vue';
import UncommissionedDevices from '@/components/configuration/UncommissionedDevices.vue';
import DeviceEditModal from '@/components/modal/DeviceEditModal.vue';
import FileDownloadButton from '@/components/configuration/FileDownloadButton.vue';
import FileUpload from '@/components/configuration/FileUpload.vue';
import ToolTip from '@/components/common/ToolTip.vue';

import { Device, DeviceArea, DeviceEdit, Floor } from '@/interfaces/locations';
import { Room, Zone } from '@/interfaces/lighting';
import {
	CommissionData,
	Controller,
	LimboController,
	ControllerDeletion,
} from '@/interfaces/controllers';
import { DeviceType } from '@/interfaces/types';
import { AppliedRule, Rule, RulespecMovementSettings } from '@/interfaces/rules';
import permissions from '@/lib/permissions';
import { Alert } from '@/interfaces/alerts';
import { AxiosError } from 'axios';

interface ComponentData {
	deviceZones: string[] | string | null;
	selectedController: Controller | null;
	selectedDevice: Device | null;
}

interface LocationName {
	name: string;
}

export interface DeviceRow {
	id: number;
	name: string;
	floor: string;
	room: string;
	zone: string;
	type: string;
	off: boolean;
	appliedRule: string;
	identify: boolean;
	controllerName: string;
	controllerId: number;
	category: string;
	unassign: 'on' | 'disabled';
}

export default Vue.extend({
	components: {
		SiteLuminaireList,
		SiteSensorList,
		UncommissionedDevices,
		DeviceEditModal,
		FileDownloadButton,
		FileUpload,
		ToolTip,
	},

	created() {
		this.fetchLimboControllers();
	},

	data(): ComponentData {
		return {
			selectedController: null,
			selectedDevice: null,
			deviceZones: null,
		};
	},

	computed: {
		luminaires(): Device[] {
			return this.$store.getters['devices/luminaires/getLuminaires'];
		},

		onlyLuminaires(): Device[] {
			return this.luminaires
				.filter(
					(luminaire: Device) =>
						luminaire.fk_device_type && this.luminaireFilter.includes(luminaire.fk_device_type)
				)
				.map((luminaire: Device) => luminaire);
		},

		switches(): Device[] {
			return this.$store.getters['devices/switches/getSwitches'];
		},

		deviceAreas(): number[] {
			return (this.$store.getters['locations/deviceAreas/getDeviceAreas'] as DeviceArea[]).map(
				(dA) => dA.id
			);
		},

		sensors(): Device[] {
			let sensors: Device[] = [];
			for (const luminaire of this.luminaires) {
				const sensor: number | null = luminaire.fk_device_type;
				if ((sensor && this.sensorFilter.includes(sensor)) || sensor === null) {
					let new_luminaire: Device = { ...luminaire }; // make a copy of the object
					new_luminaire.category = 'sensors'; // because we don't want to change the category of the original object
					sensors.push(new_luminaire);
				}
			}
			return [...this.switches, ...sensors];
		},

		devices(): Device[] {
			return [...this.switches, ...this.sensors, ...this.luminaires];
		},

		userCanReadLuminaires(): boolean {
			return permissions.hasPermission(this.$store.getters['usermanagement/user/getRole'], [
				'luminaires',
				'read',
			]);
		},

		userCanReadControllers(): boolean {
			return permissions.hasPermission(this.$store.getters['usermanagement/user/getRole'], [
				'controllers',
				'read',
			]);
		},

		userCanWriteLuminaires(): boolean {
			return permissions.hasPermission(this.$store.getters['usermanagement/user/getRole'], [
				'luminaires',
				'write',
			]);
		},

		userCanWriteControllers(): boolean {
			return permissions.hasPermission(this.$store.getters['usermanagement/user/getRole'], [
				'controllers',
				'write',
			]);
		},

		luminaireRows(): DeviceRow[] {
			return this.onlyLuminaires.map((luminaire: Device) => {
				const controller = this.$store.getters['controllers/controllers/getController'](
					luminaire.fk_controller
				);
				const floor = this.$store.getters['locations/floors/getFloor'](luminaire.fk_area);
				const zone = this.$store.getters['locations/zones/getZones'].find((z: Zone) =>
					z.devices.includes(luminaire.id)
				);
				const room = this.$store.getters['locations/rooms/getRooms'].find(
					(r: Room) => r.fk_area === floor?.id && zone?.fk_room === r.id
				);
				const appliedRules: AppliedRule[] = this.appliedRules.filter(
					(appliedRule: AppliedRule) =>
						appliedRule.fk_device === luminaire.id &&
						appliedRule.fk_rule_type != 'emergency_switch_rule_status'
				);
				const appliedEmerSwRules: AppliedRule[] = this.appliedRules.filter(
					(appliedRule: AppliedRule) =>
						appliedRule.fk_device === luminaire.id &&
						appliedRule.fk_rule_type == 'emergency_switch_rule_status'
				);
				let rule = null;
				if (appliedRules.length > 0) {
					const importantAppliedRule: AppliedRule = appliedRules.sort(
						(ruleA, ruleB) => ruleB.priority - ruleA.priority
					)[0];
					rule = this.$store.getters['activities/rules/getRule'](importantAppliedRule.fk_rule);
				}
				let appliedEmerSwRule = null;
				if (appliedEmerSwRules.length > 0) {
					appliedEmerSwRule = this.$store.getters['activities/rules/getRule'](
						appliedEmerSwRules[0].fk_rule
					);
				}

				return {
					id: luminaire.id,
					category: 'luminaires',
					name:
						controller && controller.guid
							? `${luminaire.name} - ${controller.guid.slice(controller.guid.length - 4)}`
							: luminaire.name,
					floor: floor?.name,
					room: room?.name,
					zone: zone?.name,
					channel: controller?.radio_channel,
					type: this.$store.getters['types/deviceTypes/getDeviceType'](luminaire.fk_device_type)
						?.name,
					identify:
						this.identifyRule !== undefined
							? !!this.$store.getters['activities/appliedRules/getDeviceAppliedRule'](
									luminaire.id,
									this.identifyRule!.id
							  )
							: false,
					off:
						this.offRule !== undefined
							? !!this.$store.getters['activities/appliedRules/getDeviceAppliedRule'](
									luminaire.id,
									this.offRule!.id
							  )
							: false,
					appliedRule: rule ? rule.name : '',
					emerSwRuleExists: appliedEmerSwRule,
					controllerName: controller?.guid,
					controllerId: controller?.id,
					controllerVersion: controller?.software_version,
					offset: luminaire.light_level_offset,
					unassign: this.allowUnassign(luminaire.id) ? 'on' : 'disabled',
				};
			});
		},

		luminaireFilter(): number[] {
			return this.deviceTypes
				.filter((deviceType: DeviceType) => deviceType.features.includes('dt_light_control'))
				.map((deviceType: DeviceType) => deviceType.id);
		},

		sensorFilter(): number[] {
			let correctFeatureIds: number[] = [];
			for (const deviceType of this.deviceTypes) {
				let correctType = deviceType.features.length !== 1;
				if (deviceType.features.length === 1) {
					correctType = deviceType.features[0] !== 'dt_light_control';
				}
				if (correctType) {
					correctFeatureIds.push(deviceType.id);
				}
			}
			return correctFeatureIds;
		},

		sensorsWithAreas(): Device[] {
			let sensors: Device[] = [];
			let deviceId: number[] = [];
			for (const deviceArea of [this.deviceAreas]) {
				if (deviceArea.length > 2) {
					deviceId.push(deviceArea[deviceArea.length - 1]);
				}
			}
			for (const sensor of this.sensors) {
				if (sensor.id === deviceId[0]) {
					sensor.fk_areas = true;
				}
				sensors.push(sensor);
			}
			return sensors;
		},

		sensorRows(): DeviceRow[] {
			return this.sensorsWithAreas.map((device: Device) => {
				const controller = this.$store.getters['controllers/controllers/getController'](
					device.fk_controller
				);
				let floor = this.$store.getters['locations/floors/getFloor'](device.fk_area);
				const zone = this.$store.getters['locations/zones/getZones'].find((z: Zone) =>
					z.devices.includes(device.id)
				);
				let zones: LocationName[] = [];
				let rooms: LocationName[] = [];
				let roomIds: string[] = [];

				for (const zone of this.zones) {
					if (zone.devices.includes(device.id)) {
						zones.push(zone);
						roomIds.push(zone.fk_room);
					}
				}
				if (zones.length > 1) {
					zones = [{ name: this.$t('Multiple zones').toString() }];
				}
				const checkRoomQuantity = roomIds.some((ele) => ele !== roomIds[0]);
				if (checkRoomQuantity) {
					rooms = [{ name: this.$t('Multiple rooms').toString() }];
				} else {
					rooms = [
						this.$store.getters['locations/rooms/getRooms'].find(
							(r: Room) => r.fk_area === floor?.id && zone?.fk_room === r.id
						),
					];
				}

				if (device.fk_areas) {
					floor = { name: this.$t('Multiple floors').toString() };
				}
				if (device.fk_area === null) {
					floor = { name: this.$t('No floor assigned').toString() };
				}

				const appliedRules: AppliedRule[] = this.appliedRules.filter(
					(appliedRule: AppliedRule) =>
						appliedRule.fk_device === device.id &&
						appliedRule.fk_rule_type != 'emergency_switch_rule_status'
				);
				let rule = null;
				if (appliedRules.length > 0) {
					const importantAppliedRule: AppliedRule = appliedRules.sort(
						(ruleA, ruleB) => ruleB.priority - ruleA.priority
					)[0];
					rule = this.$store.getters['activities/rules/getRule'](importantAppliedRule.fk_rule);
				}
				return {
					id: device.id,
					category: device.category,
					name:
						controller && controller.guid
							? `${device.name} - ${controller.guid.slice(controller.guid.length - 4)}`
							: device.name,
					floor: floor?.name,
					room: rooms[0]?.name,
					zone: zones[0]?.name,
					channel: controller?.radio_channel,
					type: this.$store.getters['types/deviceTypes/getDeviceType'](device.fk_device_type)?.name,
					identify:
						this.identifyRule !== undefined
							? !!this.$store.getters['activities/appliedRules/getDeviceAppliedRule'](
									device.id,
									this.identifyRule!.id
							  )
							: false,
					off: false,
					appliedRule: rule ? rule.name : '',
					controllerName: controller?.guid,
					controllerId: controller?.id,
					controllerVersion: controller?.software_version,
					unassign: this.allowUnassign(device.id) ? 'on' : 'disabled',
				};
			});
		},

		siteRules(): Rule[] | undefined {
			return this.$store.getters['activities/rules/getRules'].filter(
				(r: Rule) => r.fk_group !== null
			);
		},

		siteSensors(): string[] | undefined {
			let names: string[] = [];
			let rules: Rule[] | undefined = this.siteRules;

			if (rules) {
				let ruleBody = rules.map((r) => r.rulespec[0].rules);
				for (let rule of ruleBody) {
					for (let rulePart of rule) {
						if (['movement_settings', 'relative_light_settings'].includes(rulePart['type'])) {
							for (let sensor of (rulePart as RulespecMovementSettings)['sensors']) {
								names.push(sensor);
							}
						}
					}
				}
			}
			return names;
		},

		limboControllers(): LimboController[] {
			return this.$store.getters['controllers/limboControllers/getLimboControllers'];
		},

		missingDeviceControllers(): Controller[] {
			return this.$store.getters['controllers/controllers/getControllers'].filter(
				(controller: Controller) => {
					return (
						!this.$store.getters['devices/luminaires/getLuminaires'].some(
							(luminaire: Device) => luminaire.fk_controller === controller.id
						) &&
						!this.$store.getters['devices/switches/getSwitches'].some(
							(_switch: Device) => _switch.fk_controller === controller.id
						) &&
						!this.$store.getters['devices/gateways/getGateways'].some(
							(gateway: Device) => gateway.fk_controller === controller.id
						)
					);
				}
			);
		},

		combinedUncommissionedControllers(): (Controller | LimboController)[] {
			return [...this.limboControllers, ...this.missingDeviceControllers];
		},

		deviceTypes(): DeviceType[] {
			return this.$store.getters['types/deviceTypes/getDeviceTypes'];
		},

		luminaireTypes(): DeviceType[] {
			return this.deviceTypes.filter((dt) => dt.category === 'luminaires');
		},

		controllers(): Controller[] {
			return this.$store.getters['controllers/controllers/getControllers'];
		},

		floors(): Floor[] {
			const selectedSite = this.$store.getters['tenancy/sites/getSelectedSite'];

			if (selectedSite) {
				return this.$store.getters['locations/floors/getSiteFloors'](selectedSite.id);
			} else {
				return [];
			}
		},

		rooms(): Room[] {
			return this.$store.getters['locations/rooms/getRooms'];
		},

		zones(): Zone[] {
			return this.$store.getters['locations/zones/getZones'];
		},

		identifyRule(): Rule | undefined {
			return (
				this.$store.getters['activities/rules/getRulesCertainType']('lighting_override') as Rule[]
			).find((rule) => rule.name === 'Lighting identify override rule');
		},

		offRule(): Rule | undefined {
			return (
				this.$store.getters['activities/rules/getRulesCertainType']('lighting_override') as Rule[]
			).find((rule) => rule.name === 'Lighting off override rule');
		},

		gateways(): Device[] {
			return this.$store.getters['devices/gateways/getGateways'];
		},

		appliedRules(): AppliedRule[] {
			return this.$store.getters['activities/appliedRules/getAppliedRules'];
		},

		alerts(): Alert[] {
			return this.$store.getters['activities/alerts/getAlerts'];
		},

		globalRefresh(): boolean {
			return this.$store.getters['tools/loading/getGlobalRefresh'];
		},
	},

	watch: {
		globalRefresh: {
			handler(refresh: boolean): void {
				if (!refresh) {
					this.fetchLimboControllers();
					this.$store.dispatch('locations/deviceAreas/fetchDeviceAreas');
				}
			},
		},
	},

	methods: {
		allowUnassign(deviceId: number): boolean {
			let selectedDevice: Device =
				this.luminaires.find((l) => l.id === deviceId)! ||
				this.sensors.find((l) => l.id === deviceId)!;
			if (!selectedDevice) {
				selectedDevice = this.sensors.find((l) => l.id === deviceId)!;
			}
			if (selectedDevice && selectedDevice!.fk_controller !== null) {
				const selectedController: Controller | null =
					this.controllers.find((c) => c.id === selectedDevice!.fk_controller) ?? null;
				if (selectedController) {
					return !this.siteSensors?.includes(selectedController.guid);
				}
			}
			return true;
		},

		fetchLimboControllers(): void {
			this.gateways.forEach((gateway: Device) => {
				this.$store.dispatch('controllers/limboControllers/fetchLimboControllers', {
					gatewayId: gateway.id,
				});
			});
		},
		async commissionDevices(commissions: CommissionData[]): Promise<void> {
			this.$store.dispatch('tools/loading/changeLoadingStatus');
			// Just in case - this promise may take a while. Navigating away from this page will
			// mean `this === null` later on

			let filteredSwitchTypes: DeviceType[] = [];
			let filteredLuminaireTypes: DeviceType[] = [];
			for (const deviceType of this.deviceTypes) {
				const hasSensorFeature = deviceType.features.includes('dt_movement_count');
				const hasSwitchFeature = deviceType.features.includes('battery_resource');
				const hasEmergencySwitchFeature = deviceType.features.includes('user_button_resource');
				const hasLuminaireFeature = deviceType.features.includes('dt_light_control');

				if (hasEmergencySwitchFeature || hasSwitchFeature) {
					filteredSwitchTypes.push(deviceType);
				}
				if (hasLuminaireFeature || (hasSensorFeature && !hasSwitchFeature)) {
					filteredLuminaireTypes.push(deviceType);
				}
			}

			let devices: CommissionData[] = [];
			for (const device of commissions) {
				const id: number | null = Number(device.fk_device_type);

				for (const type of filteredLuminaireTypes) {
					const typeId: number | null = type.id;
					if (typeId && typeId === id) {
						device.category = 'luminaires';
						device.apiEndpoint = 'addLuminaire';
						devices.push(device);
					}
				}
				for (const type of filteredSwitchTypes) {
					const typeId: number | null = type.id;
					if (typeId && typeId === id) {
						device.category = 'switch es';
						device.apiEndpoint = 'addSwitch';
						devices.push(device);
					}
				}
			}
			const store = this.$store;
			for (const commission of devices) {
				try {
					if (commission.type === 'limbo_controller') {
						await store.dispatch('controllers/limboControllers/commissionLimboControllers', {
							guids: [commission.guid],
							limboIds: [commission.id],
							gatewayId: commission.gateway_id,
						});
						commission.id = store.getters['controllers/controllers/getControllerByGUID'](
							commission.guid
						).id;
					}
					let devicePayload: DeviceEdit = {
						name: commission.name,
						description: '',
						category: commission.category,
						fk_device_type: Number(commission.fk_device_type),
						fk_controller: Number(commission.id),
					};
					if (commission.fk_area) {
						devicePayload.fk_area = Number(commission.fk_area);
					}
					const device: Device | undefined = await store.dispatch(
						`devices/${commission.category}/${commission.apiEndpoint}`,
						{
							payload: devicePayload,
							returnData: !!commission.fk_area,
						}
					);
					if (commission.fk_zone && device) {
						const oldZone: Zone = store.getters['locations/zones/getZone'](commission.fk_zone);
						await store.dispatch('locations/zones/modifyZone', {
							id: oldZone.id,
							payload: { devices: [...oldZone.devices, device.id] },
						});
					}

					this.$store.dispatch('activities/appliedRules/fetchAppliedRules');
				} catch (error) {
					this.$store.dispatch('tools/messageHandling/addErrorMessage', {
						id: 'error-commission-luminaire',
						message: (error as AxiosError<{ message: string }>).response!.data.message,
					});
				}
			}
			this.$store.dispatch('tools/loading/changeLoadingStatus');
		},

		showDeviceModal(): void {
			this.$bvModal.show('modal-assign-device');
		},

		async onDeviceClicked(row: DeviceRow) {
			this.selectedDevice = await this.devices.find((l) => l.id === row.id)!;
			if (row.zone) {
				const zones = [];
				for (const zone of this.zones) {
					if (zone.devices.includes(row.id)) {
						zones.push(zone.name);
					}
				}
				this.deviceZones = zones;
			} else {
				this.deviceZones = null;
			}

			if (this.selectedDevice!.fk_controller !== null) {
				this.selectedController =
					(await this.controllers.find((c) => c.id === this.selectedDevice!.fk_controller)) ?? null;
			}

			// eslint-disable-next-line @typescript-eslint/no-explicit-any
			(this.$refs.deviceEditModal as any).show();
		},

		async onModifyDevice(
			device: Partial<Device> & { id: number },
			category: string,
			apiEndpoint: string
		) {
			this.$store.dispatch('tools/loading/changeLoadingStatus');
			try {
				await this.$store.dispatch(`devices/${category}/${apiEndpoint}`, {
					id: device.id,
					payload: device,
				});
				this.$store.dispatch('activities/rules/fetchRules');
				this.$store.dispatch('activities/appliedRules/fetchAppliedRules');
				// eslint-disable-next-line @typescript-eslint/no-explicit-any
				(this.$refs.deviceEditModal as any).hide();
			} catch (error) {
				this.$store.dispatch('tools/messageHandling/addErrorMessage', {
					id: 'error-modify-device',
					message: (error as AxiosError<{ message: string }>).response!.data.message,
				});
			}

			this.$store.dispatch('tools/loading/changeLoadingStatus');
		},

		async changeOff(id: number, off: boolean): Promise<void> {
			this.$store.dispatch('tools/loading/changeLoadingStatus');
			try {
				await this.removeAppliedRule(id, 'identify');
				if (off) {
					await this.$store.dispatch('activities/appliedRules/addAppliedRule', {
						params: {
							fk_rule: this.offRule!.id,
							fk_device: id,
						},
					});
				} else {
					await this.removeAppliedRule(id, 'off');
				}
			} catch (error) {
				this.$store.dispatch('tools/messageHandling/addErrorMessage', {
					id: 'error-change-off',
					message: (error as AxiosError<{ message: string }>).response!.data.message,
				});
			}
			this.$store.dispatch('tools/loading/changeLoadingStatus');
		},

		async changeIdentify(id: number, identify: boolean): Promise<void> {
			this.$store.dispatch('tools/loading/changeLoadingStatus');
			try {
				await this.removeAppliedRule(id, 'off');

				if (identify) {
					await this.$store.dispatch('activities/appliedRules/addAppliedRule', {
						params: {
							fk_rule: this.identifyRule!.id,
							fk_device: id,
						},
					});
				} else {
					await this.removeAppliedRule(id, 'identify');
				}
			} catch (error) {
				this.$store.dispatch('tools/messageHandling/addErrorMessage', {
					id: 'error-change-identify',
					message: (error as AxiosError<{ message: string }>).response!.data.message,
				});
			}
			this.$store.dispatch('tools/loading/changeLoadingStatus');
		},

		async removeAppliedRule(id: number, type: string): Promise<void> {
			let appliedRule: AppliedRule | undefined;

			switch (type) {
				case 'off':
					appliedRule = this.$store.getters['activities/appliedRules/getDeviceAppliedRule'](
						id,
						this.offRule!.id
					);
					break;
				case 'identify':
					appliedRule = this.$store.getters['activities/appliedRules/getDeviceAppliedRule'](
						id,
						this.identifyRule!.id
					);
					break;
				default:
					appliedRule = undefined;
			}

			if (appliedRule) {
				await this.$store.dispatch('activities/appliedRules/removeAppliedRule', {
					id: appliedRule.id,
				});
			}
		},

		cleanModalData(): void {
			this.selectedDevice = null;
			this.selectedController = null;
		},

		async removeDevice(
			{
				selectedDeviceId,
				selectedControllerId,
				selectedCategory,
			}: {
				selectedDeviceId: number;
				selectedControllerId: number;
				selectedCategory: string;
			},
			apiEndpoint: string
		): Promise<void> {
			this.$store.dispatch('tools/loading/changeLoadingStatus');
			try {
				const zone = this.zones.find((zone: Zone) => zone.devices.includes(selectedDeviceId));
				if (zone) {
					const devices = zone.devices.filter((deviceId: number) => deviceId !== selectedDeviceId);

					await this.$store.dispatch('locations/zones/modifyZone', {
						id: zone!.id,
						payload: { devices },
					});
				}
				await this.$store.dispatch(`devices/${selectedCategory}/${apiEndpoint}`, {
					id: selectedDeviceId,
				});

				await this.$store.dispatch('controllers/controllers/deleteController', {
					id: selectedControllerId,
				});

				this.$store.dispatch('activities/appliedRules/fetchAppliedRules');

				const deviceAlerts: Alert[] = this.alerts.filter(
					(alert) => alert.fk_device === selectedDeviceId
				);
				for (const deviceAlert of deviceAlerts) {
					this.$store.dispatch('activities/alerts/removeAlertLocally', { id: deviceAlert.id });
				}
			} catch (error) {
				this.$store.dispatch('tools/messageHandling/addErrorMessage', {
					id: 'error-remove-luminaire',
					message: (error as AxiosError<{ message: string }>).response!.data.message,
				});
			}
			this.$store.dispatch('tools/loading/changeLoadingStatus');
		},

		async deleteUncommissionedDevices(uncommissionedDevices: ControllerDeletion[]): Promise<void> {
			this.$store.dispatch('tools/loading/changeLoadingStatus');
			try {
				for (const uncommissionedDevice of uncommissionedDevices) {
					if (uncommissionedDevice.type === 'limbo_controller') {
						await this.$store.dispatch('controllers/limboControllers/deleteLimboController', {
							id: uncommissionedDevice.id,
							gateway_id: uncommissionedDevice.gateway_id,
							guid: uncommissionedDevice.guid,
						});
					} else {
						await this.$store.dispatch('controllers/controllers/deleteController', {
							id: uncommissionedDevice.id,
						});
					}
				}
			} catch (error) {
				this.$store.dispatch('tools/messageHandling/addErrorMessage', {
					id: 'error-delete-zone',
					message: (error as AxiosError<{ message: string }>).response!.data.message,
				});
			}
			this.$store.dispatch('tools/loading/changeLoadingStatus');
		},

		async getExcelFile(): Promise<Blob> {
			const response = await Vue.axios.get(`${process.env.VUE_APP_API_URL}/lighting/excel`, {
				responseType: 'blob',
			});
			if (response && response.status === 200) {
				return new Blob([response.data], { type: 'application/vnd.ms-excel' });
			}
			throw new Error('Could not get file');
		},

		async sendFile(file: FormData) {
			/*
            All known backend errors. This section is picked up by vue-i18n. DO NOT REMOVE.

            // Validation errors
            this.$t('Controller ID already in use on another site');
            this.$t('Controller ID already in use. Can not upload controllers with duplicate IDs');
            this.$t('Invalid zone');
            this.$t('Invalid device type');
            this.$t('Empty column: "GUID"');
            this.$t('Empty column: "Device name"');
            this.$t('Empty column: "Zone assignment"');
            this.$t('Empty column: "Device type"');

            // Developer messages
            this.$t('This endpoint can be used only with client and site token');
            this.$t('Unable to open workbook because of invalid format');
            this.$t('Mistakes or missing information in excel file');
        */
			this.$store.dispatch('tools/loading/changeLoadingStatus');
			try {
				await Vue.axios.post(`${process.env.VUE_APP_API_URL}/lighting/excel`, file, {
					headers: {
						'Content-Type': 'multipart/form-data',
					},
				});

				this.$store.dispatch('tools/messageHandling/addMessage', {
					id: 'success-file-status',
					message: this.$t('Excel upload was successful').toString(),
					type: 'success-alt',
					delay: 60000,
				});

				await Promise.all([
					this.$store.dispatch('controllers/controllers/fetchControllers'),
					this.$store.dispatch('devices/luminaires/fetchLuminaires'),
					this.$store.dispatch('devices/switches/fetchSwitches'),
					this.$store.dispatch('locations/zones/fetchZones'),
					this.$store.dispatch('activities/appliedRules/fetchAppliedRules'),
				]);
			} catch (error) {
				const errors = Object.entries(
					((error as AxiosError<{ message: string; data?: { errors: string[] } }>).response!.data
						.data?.errors ?? {}) as {
						[row: string]: string[];
					}
				)
					.map(([row, errors]) =>
						errors.map((error) => this.$t('Row {row}: {error}', { row, error })).join('\n')
					)
					.join('\n');
				this.$store.dispatch('tools/messageHandling/addErrorMessage', {
					id: 'error-file-status',
					message: errors,
				});
			}
			this.$store.dispatch('tools/loading/changeLoadingStatus');
		},

		errorFileStatus(errorMessage: string): void {
			this.$store.dispatch('tools/messageHandling/addErrorMessage', {
				id: 'error-file-status',
				message: errorMessage,
			});
		},
	},
});
